import React, { useMemo, useState } from 'react';
import { Input, Modal } from 'antd';
import {
  PanelSection,
  PanelMetaItem,
  PanelZoneSection,
  PanelRequirementBox,
} from '../compliance-ui/panelSidebarPrimitives.jsx';
import { statusBadgeClass, statusLabel } from '../compliance-ui/complianceIssuePresenters.js';
import { ReferencePill } from '../compliance-ui/ComplianceReferencePills.jsx';
import {
  ALIGNMENT_STATUS,
  PLATFORM_THEME,
} from '../../services/communications/communicationsTypes.js';
import {
  detectAlignmentForThread,
  formatRelativeTime,
} from '../../services/communications/communicationsPresenters.js';

function IssuesTab({ issues, activeIssueId, onSelectIssue }) {
  const openCount = (issues || []).filter((i) => i.status === 'open').length;

  return (
    <div className="panel-sidebar communications-issues-sidebar">
      <div className="panel-sidebar__header">
        <div className="panel-sidebar__header-top">
          <span className="panel-sidebar__id">Communications</span>
          <span className="panel-sidebar__context">
            {openCount ? `${openCount} open` : 'Alignment tracking'}
          </span>
        </div>
        <h2 className="panel-sidebar__title">Issues</h2>
        <p className="panel-sidebar__subtitle">
          Cross-platform conflicts and open alignment items
        </p>
      </div>

      <div className="communications-issues-list">
        {(issues || []).map((issue) => (
          <button
            key={issue.id}
            type="button"
            className={`communications-issue-card${activeIssueId === issue.id ? ' active' : ''}`}
            onClick={() => onSelectIssue?.(issue)}
          >
            <div className="communications-issue-card__top">
              <span className="communications-issue-card__code">{issue.code}</span>
              <span className={statusBadgeClass(issue.status)}>
                {statusLabel(issue.status)}
              </span>
            </div>
            <div className="communications-issue-card__topic">{issue.topic}</div>
            <div className="communications-issue-card__meta">
              <span className="panel-chip">{issue.platform}</span>
              {issue.severity ? (
                <span className={`panel-chip panel-chip--${issue.severity}`}>
                  {issue.severity}
                </span>
              ) : null}
            </div>
          </button>
        ))}
        {!issues?.length ? (
          <p className="communications-issues-empty">No communication issues.</p>
        ) : null}
      </div>
    </div>
  );
}

function DetailTab({
  item,
  allItems,
  activeConflict,
  activeThreadKey,
  alignmentAnswer,
  alignmentLoading,
  onAsk,
  onRecordDecision,
}) {
  const [question, setQuestion] = useState('');
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionText, setDecisionText] = useState('');

  const conflict = useMemo(() => {
    if (activeConflict) return activeConflict;
    if (item?.threadKey) return detectAlignmentForThread(allItems, item.threadKey);
    if (activeThreadKey) return detectAlignmentForThread(allItems, activeThreadKey);
    return null;
  }, [activeConflict, activeThreadKey, allItems, item]);

  const threadTopic = conflict?.threadTopic || item?.threadTopic;
  const isConflict =
    conflict?.status === ALIGNMENT_STATUS.CONFLICT ||
    conflict?.status === ALIGNMENT_STATUS.PARTIAL;
  const isAligned = conflict?.status === ALIGNMENT_STATUS.ALIGNED;

  if (!item && !conflict) {
    return (
      <div className="panel-sidebar communications-detail-sidebar">
        <div className="panel-sidebar__header">
          <h2 className="panel-sidebar__title">Message &amp; alignment</h2>
          <p className="panel-sidebar__subtitle">
            Select a message or issue to review platform alignment and details.
          </p>
        </div>
      </div>
    );
  }

  const theme = item ? PLATFORM_THEME[item.source] : null;

  return (
    <div className="panel-sidebar communications-detail-sidebar">
      <div className="panel-sidebar__header">
        {item ? (
          <>
            <div className="panel-sidebar__header-top">
              <span
                className={`comm-source-badge comm-source-badge--${item.source}`}
                style={{ '--platform-accent': theme?.accent }}
              >
                {theme?.label}
              </span>
              {isConflict ? (
                <span className="badge badge-non_compliant">Conflict</span>
              ) : isAligned ? (
                <span className="badge badge-compliant">Aligned</span>
              ) : null}
            </div>
            <h2 className="panel-sidebar__title">{item.subject}</h2>
            <p className="panel-sidebar__subtitle">{item.threadTopic}</p>
          </>
        ) : (
          <>
            <h2 className="panel-sidebar__title">{threadTopic || 'Thread alignment'}</h2>
            <p className="panel-sidebar__subtitle">Cross-platform summary</p>
          </>
        )}
      </div>

      <div className="panel-sidebar__body">
        {conflict ? (
          <PanelZoneSection
            title="Alignment"
            accessory={
              isConflict ? (
                <span className="badge badge-non_compliant">Review needed</span>
              ) : isAligned ? (
                <span className="badge badge-compliant">Aligned</span>
              ) : null
            }
          >
            {isConflict ? (
              <PanelRequirementBox>
                Platforms may not agree on {threadTopic}. Compare positions below and record a
                team decision when resolved.
              </PanelRequirementBox>
            ) : isAligned ? (
              <PanelRequirementBox>
                All connected platforms are aligned on {threadTopic}.
              </PanelRequirementBox>
            ) : null}

            {['outlook', 'teams', 'planner'].map((src) => {
              const slice = conflict.bySource?.[src];
              const label = PLATFORM_THEME[src]?.label || src;
              return (
                <div key={src} className="communications-platform-position">
                  <div className="conflict-source-label">{label}</div>
                  <div className="evidence-box">
                    <div className="evidence-statement">
                      {slice?.decisionPosition || slice?.excerpt || 'No message on this platform for this thread.'}
                    </div>
                    {slice?.author ? (
                      <div className="evidence-location">
                        {slice.author}
                        {slice.timestamp ? ` · ${formatRelativeTime(slice.timestamp)}` : ''}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}

            {isConflict ? (
              <div className="panel-sidebar__form communications-alignment-ask">
                <div className="panel-zone-field">
                  <label className="panel-zone-field__label" htmlFor="comm-alignment-question">
                    Ask about this discrepancy
                  </label>
                  <Input
                    id="comm-alignment-question"
                    size="small"
                    placeholder="e.g. Which fire rating governs Level 3 corridor?"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onPressEnter={() => onAsk?.(question)}
                    className="panel-sidebar__input"
                  />
                </div>
                <div className="communications-alignment-ask__actions">
                  <button
                    type="button"
                    className="btn-primary btn-primary--small"
                    disabled={alignmentLoading || !question.trim()}
                    onClick={() => onAsk?.(question)}
                  >
                    Ask
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-secondary--small"
                    onClick={() => setDecisionOpen(true)}
                  >
                    Record decision
                  </button>
                </div>
                {alignmentAnswer?.summary ? (
                  <PanelRequirementBox>{alignmentAnswer.summary}</PanelRequirementBox>
                ) : null}
              </div>
            ) : null}
          </PanelZoneSection>
        ) : null}

        {item ? (
          <>
            <PanelZoneSection title="Message">
              <PanelRequirementBox>
                <div className="panel-prose" style={{ whiteSpace: 'pre-wrap' }}>
                  {item.body || item.preview}
                </div>
              </PanelRequirementBox>
            </PanelZoneSection>

            <PanelZoneSection title="Details">
              <PanelMetaItem label="From">{item.author}</PanelMetaItem>
              <PanelMetaItem label="Date">{formatRelativeTime(item.timestamp)}</PanelMetaItem>
              {item.decisionPosition ? (
                <PanelMetaItem label="Position">{item.decisionPosition}</PanelMetaItem>
              ) : null}
              {item.status ? (
                <PanelMetaItem label="Status">{item.status.replace('_', ' ')}</PanelMetaItem>
              ) : null}
            </PanelZoneSection>

            {(item.linkedRefs || []).length ? (
              <PanelSection title="Linked requirements">
                <div className="panel-sidebar__pills finding-row">
                  {item.linkedRefs.map((ref) => (
                    <ReferencePill key={ref} reference={{ label: ref, value: ref }} />
                  ))}
                </div>
              </PanelSection>
            ) : null}

            {(item.attachments || []).length ? (
              <PanelSection title="Attachments">
                {item.attachments.map((f) => (
                  <PanelMetaItem key={f} label="File" fullWidth>
                    {f}
                  </PanelMetaItem>
                ))}
              </PanelSection>
            ) : null}
          </>
        ) : null}
      </div>

      <Modal
        title="Record aligned decision"
        open={decisionOpen}
        onOk={async () => {
          await onRecordDecision?.(decisionText);
          setDecisionOpen(false);
          setDecisionText('');
        }}
        onCancel={() => setDecisionOpen(false)}
        okButtonProps={{ disabled: !decisionText.trim() }}
      >
        <Input.TextArea
          rows={3}
          value={decisionText}
          onChange={(e) => setDecisionText(e.target.value)}
          placeholder="Enter the decision the team agrees on"
          className="panel-sidebar__input"
        />
      </Modal>
    </div>
  );
}

export default function CommunicationsRightPanel({
  tab,
  issues,
  activeIssueId,
  onSelectIssue,
  selectedItem,
  allItems,
  activeConflict,
  activeThreadKey,
  alignmentAnswer,
  alignmentLoading,
  onAsk,
  onRecordDecision,
}) {
  return (
    <div className="compliance-dashboard communications-panel-host">
      {tab === 'issues' ? (
        <IssuesTab
          issues={issues}
          activeIssueId={activeIssueId}
          onSelectIssue={onSelectIssue}
        />
      ) : (
        <DetailTab
          item={selectedItem}
          allItems={allItems}
          activeConflict={activeConflict}
          activeThreadKey={activeThreadKey}
          alignmentAnswer={alignmentAnswer}
          alignmentLoading={alignmentLoading}
          onAsk={onAsk}
          onRecordDecision={onRecordDecision}
        />
      )}
    </div>
  );
}
